import { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2 } from "lucide-react";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Modal, Field, inputCls, useDirectory } from "./common";

const localizer = dateFnsLocalizer({
  format, parse, startOfWeek: () => startOfWeek(new Date()), getDay, locales: { "en-US": enUS },
});

const SERVICE_TYPES = ["Lawn Maintenance", "Mowing", "Edging", "Plant Installation", "Hardscaping", "Drainage Solutions", "Cleanup", "Other"];
const empty = { customer_id: "", property_id: "", employee_id: "", service_type: "Mowing", service_date: "", description: "", notes: "", color: "#005C29" };

export default function ScheduleCalendar({ role }) {
  const isAdmin = role === "admin";
  const [jobs, setJobs] = useState([]);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  const [modal, setModal] = useState(null); // {mode, data}
  const [props, setProps] = useState([]);
  const { customers, employees, custName, empName } = useDirectory();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/jobs"); setJobs(data); } catch { setJobs([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (modal?.data?.customer_id) {
      api.get(`/properties?customer_id=${modal.data.customer_id}`).then(({ data }) => setProps(data)).catch(() => setProps([]));
    } else setProps([]);
  }, [modal?.data?.customer_id]);

  const events = useMemo(() => jobs.map((j) => ({
    id: j.id, title: `${j.service_type}${isAdmin ? " · " + custName(j.customer_id) : ""}${j.status === "completed" ? " ✓" : ""}`,
    start: new Date(j.service_date), end: new Date(new Date(j.service_date).getTime() + 60 * 60 * 1000),
    resource: j,
  })), [jobs, isAdmin, customers]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = (slot) => {
    if (!isAdmin) return;
    const d = slot?.start ? new Date(slot.start) : new Date();
    setModal({ mode: "new", data: { ...empty, service_date: toLocal(d) } });
  };
  const openEdit = (ev) => setModal({ mode: "edit", data: { ...ev.resource, service_date: toLocal(new Date(ev.resource.service_date)) } });

  const save = async () => {
    const d = modal.data;
    const payload = { ...d, service_date: new Date(d.service_date).toISOString() };
    try {
      if (modal.mode === "new") await api.post("/jobs", payload);
      else await api.put(`/jobs/${d.id}`, payload);
      toast.success("Job saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/jobs/${modal.data.id}`); toast.success("Job deleted"); setModal(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const markComplete = async (jobId) => {
    try { await api.post(`/jobs/${jobId}/complete`); toast.success("Marked complete — customer notified"); setModal(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const set = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  return (
    <div data-testid="schedule-calendar">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-600 text-charcoal">Schedule</h2>
        {isAdmin && (
          <button onClick={() => openNew()} data-testid="new-job-btn" className="inline-flex items-center gap-2 bg-emerald text-white px-4 py-2.5 rounded-sm text-sm font-600 hover:bg-emerald-dark">
            <Plus className="w-4 h-4" /> New Job
          </button>
        )}
      </div>
      <div className="bg-white border border-border rounded-sm p-4" style={{ height: 640 }}>
        <Calendar localizer={localizer} events={events} startAccessor="start" endAccessor="end"
          views={["month", "week"]} view={view} onView={setView} date={date} onNavigate={setDate}
          selectable={isAdmin} onSelectSlot={openNew} onSelectEvent={openEdit}
          eventPropGetter={(e) => ({ style: { backgroundColor: e.resource.color || "#005C29", border: "none", opacity: e.resource.status === "completed" ? 0.6 : 1 } })}
          style={{ height: "100%" }} />
      </div>

      {modal && (
        <Modal title={modal.mode === "new" ? "New Job" : "Edit Job"} onClose={() => setModal(null)} testid="job-modal">
          {isAdmin && (
            <>
              <Field label="Customer">
                <select className={inputCls} data-testid="job-customer" value={modal.data.customer_id} onChange={(e) => set("customer_id", e.target.value)}>
                  <option value="">Select customer…</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
                </select>
              </Field>
              <Field label="Property">
                <select className={inputCls} data-testid="job-property" value={modal.data.property_id} onChange={(e) => set("property_id", e.target.value)}>
                  <option value="">Select property…</option>
                  {props.map((p) => <option key={p.id} value={p.id}>{p.label} — {p.address}</option>)}
                </select>
              </Field>
              <Field label="Assigned Employee">
                <select className={inputCls} data-testid="job-employee" value={modal.data.employee_id} onChange={(e) => set("employee_id", e.target.value)}>
                  <option value="">Unassigned</option>
                  {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name || emp.email}</option>)}
                </select>
              </Field>
            </>
          )}
          <Field label="Service Type">
            <select className={inputCls} data-testid="job-service" value={modal.data.service_type} onChange={(e) => set("service_type", e.target.value)} disabled={!isAdmin}>
              {SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Date & Time">
            <input type="datetime-local" className={inputCls} data-testid="job-datetime" value={modal.data.service_date} onChange={(e) => set("service_date", e.target.value)} disabled={!isAdmin} />
          </Field>
          <Field label="Notes">
            <textarea rows={2} className={inputCls} data-testid="job-notes" value={modal.data.notes} onChange={(e) => set("notes", e.target.value)} disabled={!isAdmin} />
          </Field>
          {isAdmin && (
            <Field label="Color">
              <input type="color" data-testid="job-color" value={modal.data.color} onChange={(e) => set("color", e.target.value)} className="w-16 h-10 rounded-sm border border-border" />
            </Field>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            {isAdmin && <button onClick={save} data-testid="job-save-btn" className="bg-emerald text-white px-5 py-2.5 rounded-sm font-600 hover:bg-emerald-dark">Save</button>}
            {modal.mode === "edit" && modal.data.status !== "completed" && (
              <button onClick={() => markComplete(modal.data.id)} data-testid="job-complete-btn" className="inline-flex items-center gap-1.5 bg-gold text-charcoal px-5 py-2.5 rounded-sm font-600 hover:bg-gold-dark">
                <CheckCircle2 className="w-4 h-4" /> Mark Complete
              </button>
            )}
            {isAdmin && modal.mode === "edit" && (
              <button onClick={del} data-testid="job-delete-btn" className="inline-flex items-center gap-1.5 text-red-600 px-3 py-2.5 rounded-sm font-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function toLocal(d) {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}
